<template>
	<div id="app">
		<AdBlockerBait />

		<CanaryMarker />

		<AppHeader v-if="showAppHeader" />

		<NuxtPage :keepalive="{ include: ['HomePage', 'PopularPage', 'NewPage', 'JustWatchTVPage'] }" />

		<StyleInjector />

		<ModalsContainer />

		<StartPageJwtvContainer />

		<!-- Load initTrackers -->
		<!-- Load components: ConsentBanner, MobileBanner -->
		<LowPriorityComponents />
	</div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeMount, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';

import { Platform } from '@/@types/graphql-types';
import AppHeader from '@/components/nav/AppHeader.vue';

import StyleInjector from '@/components/StyleInjector';
import AdBlockerBait from '@/components/AdBlockerBait.vue';
import CanaryMarker from '@/ui-components/CanaryMarker.vue';

import {
	RegisterSessionStartDocument,
	RegisterSessionStartMutation,
	RegisterSessionStartMutationVariables,
} from '@/graphql/mutation/RegisterSessionStart.mutation';

import { GDPR_WEB_LOCALES } from '@/constants/web-locales.constant';
import { enableScroll } from '@/helpers/scroll-helper';
import { captureMessageForSentry } from '@/libraries/sentry';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useMetaStore } from '@/stores/pinia/useMetaStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useTrackingStore } from '@/stores/pinia/useTrackingStore';

import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { useLoginFlow } from '@/features/auth/composables/useLoginFlow';
import { usePlusUpgradeToast } from '@/features/jw-pro/composables/usePlusUpgradeToast';
import { useExperimentInitialization } from '@/libraries/experiment';

import '@/styles/global.scss';
import '@fortawesome/fontawesome-svg-core/styles.css';
import { ModalsContainer } from 'vue-final-modal';
import { jw_changeLocale } from '@/routing';

const LowPriorityComponents = defineAsyncComponent(() => import('@/components/LowPriorityComponents.vue'));

const StartPageJwtvContainer = defineAsyncComponent(
	() => import('@/components/experiments/startPageJwtv/StartPageJwtvContainer.vue')
);

if (import.meta.client) {
	import('@/helpers/tracking/cwv-helper').then(({ initCoreWebVitalsMonitor }) =>
		initCoreWebVitalsMonitor({ nuxt_enabled: true })
	);
}

const metaStore = useMetaStore();
const routingStore = useRoutingStore();
const languageStore = useLanguageStore();
const userStore = useUserStore();
const trackingStore = useTrackingStore();

const route = useRoute();

const showAppHeader = computed(
	() => route.name !== 'app.streaming-charts' && !/\/streaming-charts\/?$/.test(route.path)
);

useHead(() => metaStore.head);

useHead(() => {
	if (!GDPR_WEB_LOCALES.includes(languageStore.webLocale)) return {};

	return {
		link: [
			{ rel: 'preconnect', href: 'https://web.cmp.usercentrics.eu' },
			{ rel: 'dns-prefetch', href: 'https://web.cmp.usercentrics.eu' },
		],
	};
});

const { initializeExperiments } = useExperimentInitialization();
const { initLoginFlow } = useLoginFlow();
const { showUpgradeToast } = usePlusUpgradeToast();

const {
	mutate: registerSessionStart,
	onError,
	onDone,
} = useMutation<RegisterSessionStartMutation, RegisterSessionStartMutationVariables>(RegisterSessionStartDocument);

onError(error => {
	captureMessageForSentry('SSR App: Unable to fire Register Session Start Mutation', {
		where: '[SSRApp.vue] onMounted()',
		error,
	});

	// if session start fails we still need to mark experiments as loaded so anything
	// gated on `experimentsLoaded` (e.g. ads) isn't blocked forever
	initializeExperiments([]);
});

onDone(data => {
	const experiments = data?.registerSessionStart.experiments ?? [];
	initializeExperiments(
		experiments.map(el => ({
			countries: el.countries,
			languages: el.languages,
			name: el.name,
			sample: el.sample,
			variant: el.variant ?? null,
		}))
	);
});

watch(
	() => languageStore.webLocale,
	async (webLocale, oldWebLocale) => {
		if (webLocale === oldWebLocale) return;
		jw_changeLocale(webLocale, routingStore.activeDomain);
		await languageStore.initLanguage();
	},
	{ immediate: false }
);

const { isArbitrating, arbitration } = useArbitrage();
onBeforeMount(() => {
	// runs before children mount, so the bait's sessionReady always comes after
	trackingStore.setSessionEnvironmentContext({
		isArbitrating: isArbitrating.value,
		utmMedium: arbitration.value?.utm_medium,
		utmSource: arbitration.value?.utm_source,
	});
});

onMounted(async () => {
	// Session start reports the device's login state, so it must wait until the
	// login flow has settled; otherwise a token-less call registers a logout.
	await initLoginFlow().finally(() => {
		const sessionStart = registerSessionStart({
			input: {
				platform: Platform.Web,
				language: languageStore.language,
				country: languageStore.country,
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			},
		});

		showUpgradeToast();

		return sessionStart;
	});

	nextTick(() => {
		const int = setInterval(() => {
			// remove loader
			const loader = document.querySelector('#loader');
			loader?.parentNode?.removeChild(loader);
			clearInterval(int);
		}, 50);

		// reactivate scroll on page rendered
		enableScroll();
	});

	TrackingHelper.isInitializedPromise.then(() => {
		//need to initialize PASubgenres for it to populate trackers as SEO user
		TrackingHelper.refreshPermanentAudiencesSubgenres([]);
		TrackingHelper.refreshPermanentAudiences(userStore.permanentAudiences);

		// route.fullPath is for Home as first page, which has incomplete urlMetadata
		if ((routingStore.urlMetadata?.fullPath ?? route.fullPath) == null) return;

		TrackingHelper.trackAudienceRefresh(userStore.permanentAudiences);
	});
});
</script>

<style lang="scss">
@import '@/styles/variables.scss';

body {
	overflow-x: hidden;
}

#app {
	position: relative;
	z-index: 0;
	background: var(--jw-background-color) !important;
}
</style>
